<template>
  <nav class="steps">
    <template v-for="(step, idx) in steps">
      <a
        :id="'steps_' + idx"
        :key="idx"
        class="steps__item"
        :class="{
          active: currentStep - 1 == idx,
          enabled: isEnabled(idx + 1),
          'is-invalid': hasError(idx + 1),
          'is-valid': (!hasError(idx + 1) && wasTouched(idx + 1)) || wasLoaded()
        }"
        @click.prevent="setStep(idx + 1)"
      >
        <span class="steps__title"
          >{{ isEnabled(idx + 1) ? idx + 1 : "" }}
          <span class="steps__item--is-valid">
            <Icon name="check" />
          </span>
          <span class="steps__item--is-invalid">
            <Icon name="times" />
          </span>
        </span>
        <BTooltip
          v-if="isEnabled(idx + 1)"
          placement="bottom"
          :target="'steps_' + idx"
          >{{ $t(step.title) }}</BTooltip
        >
      </a>
      <span
        v-if="idx != steps.length - 1"
        :key="idx + 's'"
        class="steps__spacer"
      ></span>
    </template>
  </nav>
</template>

<script>
import { mapGetters, mapState } from "vuex";

export default {
  name: "BaseCertificationFormSteps",
  props: {
    v: {
      type: Object,
      required: true
    },
    form: {
      type: Object,
      required: true
    }
  },
  computed: {
    ...mapState("Form", ["currentStep", "steps", "formDisabled"]),
    ...mapGetters("Form", ["total", "isEnabled"])
  },
  methods: {
    wasLoaded: function() {
      return this.form.id > 0;
    },
    wasTouched: function(step) {
      return this.v[`step${step}`].$dirty;
    },
    setStep(step) {
      if (this.isEnabled(step)) {
        this.v[`step${this.currentStep}`].$touch();
        this.$store.dispatch("Form/setStep", step);
      }
    },
    hasError(step) {
      const currentStep = this.v[`step${step}`];
      const ssErrors = this.$store.state.Form.errors;

      let hasSSError = false;
      Object.getOwnPropertyNames(ssErrors).forEach(error => {
        const errorArray = error.split(".");
        if (typeof currentStep[`form.${errorArray[1]}`] !== "undefined") {
          if (ssErrors[error] != "") {
            hasSSError = true;
          }
        }
      });

      return (
        !this.formDisabled &&
        (currentStep.$error || currentStep.$anyError || hasSSError)
      );
    }
  }
};
</script>

<style lang="scss">
@import "@/styles/_variables.scss";

.steps {
  display: flex;
  align-items: stretch;
  flex-wrap: wrap;
  justify-content: space-between;
  padding: 0;
  margin: 0;
  @media (max-width: 640px) {
    padding-right: 30px;
  }

  &__item {
    padding: 10px;
    display: block;
    cursor: pointer;
    position: relative;
    width: calc(2em + 20px);
  }

  &__spacer {
    border-bottom: 1px solid $col-polmed-gray;
    display: block;
    max-width: 100%;
    align-self: center;
    flex: 1 1 auto;
    width: 15px;
    @media (max-width: 640px) {
      width: 30%;
      &:nth-child(3n + 3) {
        display: none;
      }
    }
    @media (max-width: 400px) {
      width: 22%;
    }
  }

  &__item--is-invalid,
  &__item--is-valid {
    background: rgba(255, 255, 255, 0);
    position: absolute;
    top: 50%;
    transform: translate(0, -50%);
    left: 40%;
    margin-left: -10px;
    opacity: 0;
    width: 30px;
    transition: all 0.3s;
    svg {
      width: 30px;
    }
  }

  .is-invalid {
    .steps__item--is-invalid {
      background: rgba(255, 255, 255, 1);
      display: block;
      left: 100%;
      opacity: 1;
      svg {
        fill: $col-polmed-red;
      }
    }
  }
  .is-valid {
    .steps__item--is-valid {
      background: rgba(255, 255, 255, 1);
      display: block;
      left: 100%;
      opacity: 1;
      svg {
        fill: $col-polmed-green;
      }
    }
  }

  &__title {
    font-weight: bold;
    display: block;
    border-radius: 50%;
    background: $col-polmed-gray;
    width: 2em;
    height: 2em;
    text-align: center;
    line-height: 2.1em;
    color: white;
    opacity: 0.3;
    transition: all $transition-duration;
    cursor: not-allowed;

    .enabled &:hover {
      background: $col-polmed-blue;
      color: white;
    }

    .active & {
      background: $col-polmed-navy;
      color: white;
      opacity: 1;
    }

    .enabled & {
      opacity: 1;
      cursor: pointer;
    }

    .is-invalid & {
      background: lighten($col-polmed-red, 20%);
    }

    .is-invalid.active & {
      background: $col-polmed-red;
    }

    .is-invalid &:hover {
      background: darken($col-polmed-red, 10%);
    }
  }
}
</style>
